<!--
 * Path: packages/frontend/src/features/browser/forms/cairoMatrix/CairoMatrix.vue
 * Task: Wrapper for CAIRO matrix using FormBlockWrapper.
-->
<template>
  <FormBlockWrapper
    :is-editing="isEditing"
    @update:is-editing="handleToggleEdit"
    :title="field.name || ''"
    :info="field.info"
    :can-edit="canEdit"
    :is-saving="uiStore.loading.save"
    @save="saveChanges"
    @show-info="handleShowInfo"
  >
    <template #read>
      <CairoMatrixRead
        :field="field"
        :hide-header="true"
        @show-info="handleShowInfo"
      />
    </template>
    <template #edit>
      <CairoMatrixEdit :field="field" :hide-header="true" />
    </template>
  </FormBlockWrapper>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { usePermissions } from '@/core/composables/usePermissions';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { FormContextKey } from '@/core/keys';
import FormBlockWrapper from '@/core/components/forms/FormBlockWrapper.vue';
import CairoMatrixRead from './CairoMatrixRead.vue';
import CairoMatrixEdit from './CairoMatrixEdit.vue';

interface FieldProps {
  name?: string;
  info?: string;
}

const { field } = defineProps<{ field: FieldProps }>();

const emit = defineEmits<{ (e: 'show-info', field: FieldProps): void; }>();

const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const { saveEntity } = useEntityOperations();
const { canEditWorkspace } = usePermissions();
const formContext = inject(FormContextKey, null);

const isEditing = ref(false);
const canEdit = computed(() => canEditWorkspace.value);

// --- Block-Level Edit Logic ---
const handleToggleEdit = (newVal: boolean) => {
  if (!newVal) {
    workspaceStore.resetSelectedEntity(formContext?.isSecondary || false);
  }
  isEditing.value = newVal;
};

const saveChanges = async () => {
  await saveEntity(formContext?.isSecondary || false);
  isEditing.value = false;
};

const handleShowInfo = (): void => {
  emit('show-info', field);
};
</script>
